import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const ThemeContext = createContext();
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const applyTheme = (theme, branding = null) => {
  const root = document.documentElement;
  const body = document.body;

  root.classList.remove('light', 'dark');
  root.classList.add(theme);
  localStorage.setItem('theme', theme);

  // Use custom branding colors if provided
  const primaryColor = branding?.primary_color || (theme === 'light' ? '#D9A962' : '#D4A574');
  const secondaryColor = branding?.secondary_color || (theme === 'light' ? '#C59D52' : '#C59D52');
  const bgColor = branding?.background_color || (theme === 'light' ? '#FFFCFB' : '#1a1816');
  const textColor = branding?.text_color || (theme === 'light' ? '#1A1A1A' : '#ffffff');

  // Helper to convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 212, g: 165, b: 116 };
  };

  const accentRgb = hexToRgb(primaryColor);
  const accentRgbString = `${accentRgb.r}, ${accentRgb.g}, ${accentRgb.b}`;

  // Set custom accent colors
  root.style.setProperty('--accent', primaryColor);
  root.style.setProperty('--accent-hover', secondaryColor);
  root.style.setProperty('--accent-rgb', accentRgbString);
  root.style.setProperty('--accent-15', `rgba(${accentRgbString}, 0.15)`);
  root.style.setProperty('--accent-20', `rgba(${accentRgbString}, 0.2)`);
  root.style.setProperty('--accent-50', `rgba(${accentRgbString}, 0.5)`);

  // Set CSS variables for theme colors
  if (theme === 'light') {
    // Warm off-white backgrounds - never pure white
    root.style.setProperty('--bg-primary', '#FFFCFB');      // Warm cream for main background
    root.style.setProperty('--bg-secondary', '#FFFFFF');     // Pure white for cards (elevated)
    root.style.setProperty('--bg-tertiary', '#FAFAFA');      // Subtle grey for nested areas

    // Soft borders - subtle, never harsh
    root.style.setProperty('--border-color', '#EAEAEA');
    root.style.setProperty('--border-subtle', '#F0F0F0');    // Extra subtle for dividers

    // Text colors - never pure black
    root.style.setProperty('--text-primary', textColor);     // Headers, important text (use custom or default)
    root.style.setProperty('--text-secondary', '#2D2D2D');   // Body text
    root.style.setProperty('--text-tertiary', '#6B7280');    // Muted/supporting text

    // Teal success colors for light mode
    root.style.setProperty('--success', '#0D9488');          // Teal-leaning success
    root.style.setProperty('--success-hover', '#0F766E');
    root.style.setProperty('--success-bg', 'rgba(13, 148, 136, 0.1)');

    // Subtle shadows for elevation
    root.style.setProperty('--shadow-sm', '0px 1px 2px rgba(0, 0, 0, 0.04)');
    root.style.setProperty('--shadow-md', '0px 1px 3px rgba(0, 0, 0, 0.06)');
    root.style.setProperty('--shadow-lg', '0px 2px 8px rgba(0, 0, 0, 0.08)');

    // Also apply directly to body for immediate update
    body.style.backgroundColor = bgColor;
    body.style.color = textColor;
  } else {
    // Dark theme
    root.style.setProperty('--bg-primary', bgColor);
    root.style.setProperty('--bg-secondary', '#2a2520');
    root.style.setProperty('--bg-tertiary', '#1f1d1b');
    root.style.setProperty('--border-color', '#3a352f');
    root.style.setProperty('--border-subtle', '#2f2a24');
    root.style.setProperty('--text-primary', textColor);
    root.style.setProperty('--text-secondary', '#d1d5db');
    root.style.setProperty('--text-tertiary', '#9ca3af');

    // Success colors for dark mode
    root.style.setProperty('--success', '#10B981');
    root.style.setProperty('--success-hover', '#059669');
    root.style.setProperty('--success-bg', 'rgba(16, 185, 129, 0.15)');

    // Shadows for dark mode (more subtle)
    root.style.setProperty('--shadow-sm', '0px 1px 2px rgba(0, 0, 0, 0.2)');
    root.style.setProperty('--shadow-md', '0px 2px 4px rgba(0, 0, 0, 0.3)');
    root.style.setProperty('--shadow-lg', '0px 4px 12px rgba(0, 0, 0, 0.4)');

    // Also apply directly to body for immediate update
    body.style.backgroundColor = bgColor;
    body.style.color = textColor;
  }

  // Force browser to recalculate styles (trigger reflow)
  // eslint-disable-next-line no-unused-expressions
  root.offsetHeight;
  // eslint-disable-next-line no-unused-expressions
  body.offsetHeight;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage first, default to 'dark'
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem('theme');
      const initialTheme = savedTheme || 'dark';
      // Apply theme immediately on mount
      applyTheme(initialTheme);
      return initialTheme;
    }
    return 'dark';
  });
  const [branding, setBranding] = useState(null);
  const [brandingLoaded, setBrandingLoaded] = useState(false);

  // Fetch branding settings on mount
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setBrandingLoaded(true);
          return;
        }

        const response = await axios.get(`${API_URL}/branding`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        setBranding(response.data);
        // Apply theme with branding
        applyTheme(theme, response.data);
      } catch (error) {
        console.error('Error fetching branding:', error);
      } finally {
        setBrandingLoaded(true);
      }
    };

    fetchBranding();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Apply theme whenever it changes (with branding if available)
    if (brandingLoaded) {
      applyTheme(theme, branding);
    }
    // Force React to re-render by triggering a state update
    // This ensures components using CSS variables re-render
    if (typeof window !== 'undefined') {
      // Small delay to ensure CSS variables are set before re-render
      setTimeout(() => {
        // Force a reflow to ensure styles are applied
        // eslint-disable-next-line no-unused-expressions
        document.body.offsetHeight;
      }, 0);
    }
  }, [theme, branding, brandingLoaded]);

  const toggleTheme = () => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === 'dark' ? 'light' : 'dark';
      // Apply theme immediately for instant feedback
      if (typeof window !== 'undefined') {
        applyTheme(newTheme, branding);
      }
      return newTheme;
    });
  };

  const refreshBranding = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_URL}/branding`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setBranding(response.data);
      applyTheme(theme, response.data);
    } catch (error) {
      console.error('Error refreshing branding:', error);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, branding, refreshBranding }}>
      {children}
    </ThemeContext.Provider>
  );
};

